import React from 'react'

import { shallowEqual, useSelector } from 'react-redux'

import useMultiFrameControls from '@context/MultiframeControls/useMultiframControl'
import { SocialSubmissionTypes, SubmissionStatuses } from '@enums'
import { useInboxFilter } from '@hooks/useInboxFilter'
import { IApplicationState } from '@store'
import {
  selectMediaObjectsHaveLicenseOptions,
  selectMediaObjectsHaveTransactedLicense,
} from '@store/mediaObjects'
import {
  selectApproveLink,
  selectDeclineLink,
  selectFeedbackLink,
  selectPreApproveLink,
  selectRescheduleLink,
  selectShortlistLink,
  selectSubmission,
  selectSubmissionMediaObjectIds,
  selectUnshortlistLink,
} from '@store/submissions'
import SubmissionCardActionBarSocialApproved from './Approved'
import SubmissionCardActionBarSocialDeclined from './Declined'
import SubmissionCardActionBarSocialInbox from './Inbox'
import SubmissionCardActionBarSocialLicenseRequests from './LicenseRequests'
import SubmissionCardActionBarSocialPreApproved from './PreApproved'

interface IProps {
  submissionId: number
  campaignId: number
}

const SubmissionCardActionBarSocial: React.FC<IProps> = ({ submissionId, campaignId }) => {
  const { activeFrameNumber } = useMultiFrameControls()
  const { currentFilter } = useInboxFilter()
  const {
    hasApproveLink,
    hasDeclineLink,
    hasFeedbackLink,
    hasPreApproveLink,
    hasRescheduleLink,
    hasShortlistLink,
    hasUnshortlistLink,
    showLicenseIcon,
    submission,
  } = useSelector((state: IApplicationState) => {
    const submission = selectSubmission(state, submissionId)

    let mediaObjectIds = selectSubmissionMediaObjectIds(state, submissionId)

    if (submission.social_submission_type === SocialSubmissionTypes.Carousel) {
      mediaObjectIds = [mediaObjectIds[activeFrameNumber - 1]]
    }

    const hasLicenseOptionsLink = selectMediaObjectsHaveLicenseOptions(state, mediaObjectIds)

    const hasTransactedLicense = selectMediaObjectsHaveTransactedLicense(state, mediaObjectIds)

    const showLicenseIcon = hasTransactedLicense || hasLicenseOptionsLink
    return {
      submission,
      showLicenseIcon,
      hasApproveLink: Boolean(selectApproveLink(state, submissionId)),
      hasDeclineLink: Boolean(selectDeclineLink(state, submissionId)),
      hasFeedbackLink: Boolean(selectFeedbackLink(state, submissionId)),
      hasRescheduleLink: Boolean(selectRescheduleLink(state, submissionId)),
      hasShortlistLink: Boolean(selectShortlistLink(state, submissionId)),
      hasUnshortlistLink: Boolean(selectUnshortlistLink(state, submissionId)),
      hasPreApproveLink: Boolean(selectPreApproveLink(state, submissionId)),
    }
  }, shallowEqual)

  if (submission.status === SubmissionStatuses.Pending) {
    return (
      <SubmissionCardActionBarSocialInbox
        hasRescheduleLink={hasRescheduleLink}
        submission={submission}
        campaignId={campaignId}
        hasShortlistLink={hasShortlistLink}
        hasUnshortlistLink={hasUnshortlistLink}
        hasDeclineLink={hasDeclineLink}
        hasFeedbackLink={hasFeedbackLink}
        hasApproveLink={hasApproveLink}
        hasPreApproveLink={hasPreApproveLink}
      />
    )
  }

  if (submission.status === SubmissionStatuses.PreApproved) {
    return (
      <SubmissionCardActionBarSocialPreApproved
        submission={submission}
        campaignId={campaignId}
        hasDeclineLink={hasDeclineLink}
        hasFeedbackLink={hasFeedbackLink}
        hasApproveLink={hasApproveLink}
      />
    )
  }

  if (
    [SubmissionStatuses.LicenseRequest, SubmissionStatuses.Licensed].includes(
      currentFilter as SubmissionStatuses,
    )
  ) {
    return (
      <SubmissionCardActionBarSocialLicenseRequests
        campaignId={campaignId}
        submission={submission}
        hasFeedbackLink={hasFeedbackLink}
      />
    )
  }

  if (currentFilter === SubmissionStatuses.Declined) {
    return (
      <SubmissionCardActionBarSocialDeclined
        campaignId={campaignId}
        showLicenseIcon={showLicenseIcon}
        submission={submission}
        hasFeedbackLink={hasFeedbackLink}
      />
    )
  }

  return (
    <SubmissionCardActionBarSocialApproved
      campaignId={campaignId}
      submission={submission}
      hasRescheduleLink={hasRescheduleLink}
      hasFeedbackLink={hasFeedbackLink}
      showLicenseIcon={showLicenseIcon}
    />
  )
}

export default SubmissionCardActionBarSocial
